import { useState, useEffect, useCallback, useRef } from "react";
import { Ban, Check, ChevronLeft, ChevronRight, Play, Search, X } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    getUnknownTags,
    getTagOptions,
    triggerBackfill,
    updateUnknownTag,
    banishTag,
    IUnknownTag,
    TagOptions,
    UnknownTagFilter,
} from "../../../interfaces/unknownTags";

// ─── Primary tag input with dropdown ─────────────────────────────────────────

function PrimaryInput({
    value,
    onChange,
    suggestions,
}: {
    value: string;
    onChange: (v: string) => void;
    suggestions: string[];
}) {
    const [open, setOpen] = useState(false);

    const filtered = value.trim()
        ? suggestions.filter((s) =>
              s.toLowerCase().includes(value.trim().toLowerCase())
          )
        : suggestions;

    return (
        <div className="relative">
            <input
                type="text"
                value={value}
                onChange={(e) => { onChange(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
                placeholder="Tag principal…"
                className="w-full border border-gray-200 rounded-sm px-2 py-1 text-xs text-gray-800 focus:outline-none focus:border-gray-400 bg-white"
            />
            {open && filtered.length > 0 && (
                <ul className="absolute z-20 top-full mt-1 left-0 w-full max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-sm shadow-sm text-xs">
                    {filtered.map((s) => (
                        <li
                            key={s}
                            onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
                            className="px-3 py-1.5 cursor-pointer hover:bg-[#F9F6F3] text-gray-700"
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ─── Subtag pill input ────────────────────────────────────────────────────────

function SubtagInput({
    value,
    query,
    onQueryChange,
    onChange,
    suggestions,
}: {
    value: string[];
    query: string;
    onQueryChange: (q: string) => void;
    onChange: (v: string[]) => void;
    suggestions: string[];
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);

    const filtered = query.trim()
        ? suggestions.filter(
              (s) =>
                  s.toLowerCase().includes(query.trim().toLowerCase()) &&
                  !value.includes(s)
          )
        : [];

    const add = (raw = query) => {
        const t = raw.trim();
        if (t && !value.includes(t)) onChange([...value, t]);
        onQueryChange("");
        setOpen(false);
    };

    const remove = (tag: string) => onChange(value.filter((t) => t !== tag));

    return (
        <div className="relative">
            <div
                className="flex flex-wrap gap-1 items-center min-h-[32px] border border-gray-200 rounded-sm px-2 py-1 bg-white focus-within:border-gray-400 cursor-text"
                onClick={() => inputRef.current?.focus()}
            >
                {value.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200"
                    >
                        {tag}
                        <button type="button" onClick={(e) => { e.stopPropagation(); remove(tag); }} className="hover:opacity-70">
                            <X className="w-2.5 h-2.5" />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => { onQueryChange(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
                        if (e.key === "Backspace" && !query && value.length > 0) remove(value[value.length - 1]);
                        if (e.key === "Escape") setOpen(false);
                    }}
                    placeholder={value.length === 0 ? "Ajouter un subtag…" : "+"}
                    className="flex-1 min-w-[80px] border-0 outline-none focus:ring-0 text-xs text-gray-700 placeholder:text-gray-400 bg-transparent"
                />
            </div>
            <p className="mt-0.5 text-[10px] text-gray-400">Entrée ou virgule pour valider chaque subtag</p>

            {open && filtered.length > 0 && (
                <ul className="absolute z-20 top-full mt-1 left-0 w-full max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-sm shadow-sm text-xs">
                    {filtered.map((s) => (
                        <li
                            key={s}
                            onMouseDown={(e) => { e.preventDefault(); add(s); }}
                            className="px-3 py-1.5 cursor-pointer hover:bg-[#F9F6F3] text-gray-700"
                        >
                            {s}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function TagRow({
    tag,
    options,
    onSaved,
    onBanished,
}: {
    tag: IUnknownTag;
    options: TagOptions;
    onSaved: (updated: IUnknownTag) => void;
    onBanished: (updated: IUnknownTag) => void;
}) {
    const [primary, setPrimary] = useState(tag.correspondingPrimary);
    const [subtags, setSubtags] = useState<string[]>(tag.correspondingSubtags);
    const [subtagQuery, setSubtagQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [banishing, setBanishing] = useState(false);

    const isDirty =
        primary !== tag.correspondingPrimary ||
        subtagQuery.trim().length > 0 ||
        JSON.stringify(subtags) !== JSON.stringify(tag.correspondingSubtags);

    const handleBanish = async () => {
        setBanishing(true);
        try {
            const updated = await banishTag(tag._id);
            onBanished(updated);
        } finally {
            setBanishing(false);
        }
    };

    const save = async () => {
        const pendingSubtag = subtagQuery.trim();
        const finalSubtags = pendingSubtag && !subtags.includes(pendingSubtag)
            ? [...subtags, pendingSubtag]
            : subtags;
        if (pendingSubtag) {
            setSubtags(finalSubtags);
            setSubtagQuery("");
        }

        setSaving(true);
        try {
            const updated = await updateUnknownTag(tag._id, {
                correspondingPrimary: primary,
                correspondingSubtags: finalSubtags,
            });
            onSaved(updated);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } finally {
            setSaving(false);
        }
    };

    const isEmpty = !tag.correspondingPrimary && tag.correspondingSubtags.length === 0;

    return (
        <tr className={`border-b border-gray-100 hover:bg-[#F9F6F3] group ${tag.banished ? "opacity-60" : ""}`}>
            {/* Tag name */}
            <td className="px-4 py-3 align-top">
                <span className="text-xs font-mono text-gray-800">{tag.tagName}</span>
                {isEmpty && !tag.banished && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        à remplir
                    </span>
                )}
                {tag.banished && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-red-50 text-red-600 border border-red-200">
                        banni
                    </span>
                )}
            </td>

            {/* Tag type */}
            <td className="px-4 py-3 align-top">
                <span
                    className={`inline-block px-2 py-0.5 rounded-sm text-[11px] font-medium border ${
                        tag.tagType === "primary"
                            ? "bg-gray-100 text-gray-700 border-gray-200"
                            : "bg-purple-50 text-purple-700 border-purple-200"
                    }`}
                >
                    {tag.tagType === "primary" ? "Principal" : "Subtag"}
                </span>
            </td>

            {/* Corresponding primary */}
            <td className="px-4 py-3 align-top min-w-[180px]">
                <PrimaryInput
                    value={primary}
                    onChange={setPrimary}
                    suggestions={options.categories}
                />
            </td>

            {/* Corresponding subtags */}
            <td className="px-4 py-3 align-top min-w-[240px]">
                <SubtagInput
                    value={subtags}
                    query={subtagQuery}
                    onQueryChange={setSubtagQuery}
                    onChange={setSubtags}
                    suggestions={options.subCategories}
                />
            </td>

            {/* Actions */}
            <td className="px-4 py-3 align-top text-right">
                <div className="flex items-center justify-end gap-2">
                    {saved ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-green-600">
                            <Check className="w-3.5 h-3.5" /> Sauvegardé
                        </span>
                    ) : (
                        <button
                            onClick={save}
                            disabled={!isDirty || saving}
                            className="px-3 py-1 text-xs rounded-sm bg-black text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                        >
                            {saving ? "…" : "Sauvegarder"}
                        </button>
                    )}
                    <button
                        onClick={handleBanish}
                        disabled={banishing}
                        title={tag.banished ? "Débannir ce tag" : "Bannir ce tag"}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            tag.banished
                                ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                                : "bg-white text-gray-500 border-gray-200 hover:border-red-300 hover:text-red-500"
                        }`}
                    >
                        <Ban className="w-3 h-3" />
                        {banishing ? "…" : tag.banished ? "Débannir" : "Bannir"}
                    </button>
                </div>
            </td>
        </tr>
    );
}

// ─── Filters ──────────────────────────────────────────────────────────────────

const FILTERS: { key: UnknownTagFilter; label: string }[] = [
    { key: "all", label: "Tous" },
    { key: "empty", label: "Vides" },
    { key: "primary_empty", label: "Principal vide" },
    { key: "subtag_empty", label: "Subtag vide" },
    { key: "filled", label: "Remplis" },
    { key: "banished", label: "Bannis" },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UnknownTags() {
    const [items, setItems] = useState<IUnknownTag[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState<UnknownTagFilter>("all");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [triggered, setTriggered] = useState(false);
    const [options, setOptions] = useState<TagOptions>({ categories: [], subCategories: [] });

    useEffect(() => {
        getTagOptions().then(setOptions).catch(() => {});
    }, []);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getUnknownTags({
                filter,
                search: debouncedSearch || undefined,
                page,
                limit: 50,
            });
            setItems(res.data);
            setTotal(res.meta.total);
            setTotalPages(res.meta.totalPages);
        } finally {
            setLoading(false);
        }
    }, [filter, debouncedSearch, page]);

    useEffect(() => { load(); }, [load]);

    const handleFilterChange = (f: UnknownTagFilter) => {
        setFilter(f);
        setPage(1);
    };

    const handleSearchChange = (v: string) => {
        setSearch(v);
        setPage(1);
    };

    const handleTrigger = async () => {
        setTriggering(true);
        try {
            await triggerBackfill();
            setTriggered(true);
            setTimeout(() => setTriggered(false), 3000);
        } finally {
            setTriggering(false);
        }
    };

    const handleSaved = (updated: IUnknownTag) => {
        setItems((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
    };

    const handleBanished = (updated: IUnknownTag) => {
        setItems((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
    };

    return (
        <div className="min-h-screen bg-[#F9F6F3]">
            <div className="max-w-screen-xl mx-auto px-6 py-8">
                <BONavigation />

                <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Tags inconnus</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Tags Google Places non reconnus par le système. Remplissez les correspondances pour qu'ils soient appliqués au prochain run.
                        </p>
                    </div>
                    <button
                        onClick={handleTrigger}
                        disabled={triggering}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                        {triggered ? (
                            <><Check className="w-4 h-4" /> Lancé</>
                        ) : (
                            <><Play className="w-4 h-4" /> {triggering ? "Lancement…" : "Lancer le backfill"}</>
                        )}
                    </button>
                </div>

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    {/* Filter pills */}
                    <div className="flex gap-1">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => handleFilterChange(f.key)}
                                className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                                    filter === f.key
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder="Rechercher un tag…"
                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-sm bg-white focus:outline-none focus:border-gray-400"
                        />
                        {search && (
                            <button
                                onClick={() => handleSearchChange("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <span className="ml-auto text-xs text-gray-400">{total} résultat{total !== 1 ? "s" : ""}</span>
                </div>

                {/* Table */}
                <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Tag Google</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Tag principal</th>
                                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Subtags</th>
                                <th className="px-4 py-2.5" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="border-b border-gray-100">
                                        {Array.from({ length: 5 }).map((__, j) => (
                                            <td key={j} className="px-4 py-3">
                                                <div className="h-4 bg-gray-100 rounded animate-pulse" />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400">
                                        Aucun tag trouvé
                                    </td>
                                </tr>
                            ) : (
                                items.map((tag) => (
                                    <TagRow key={tag._id} tag={tag} options={options} onSaved={handleSaved} onBanished={handleBanished} />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-sm bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" /> Précédent
                        </button>
                        <span className="text-xs text-gray-500">
                            Page {page} / {totalPages}
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-sm bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Suivant <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
